import { memoize } from 'underscore';

import {
  ContactStatus,
  SortDirection,
  FlowV2_Statistics,
  GetFlowsV2QueryVariables,
  GetFlowsV2Query,
} from '~/graphql/types';
import { ascend, descend, isNil, last, path } from 'ramda';
import markedForDeletionContactList from '~/components/page/Contacts/util/markedForDeletionContactList';
import { SortSettings } from '~/hooks/useSortSettings';

const text = {
  cannotSendEmails:
    'Deze flow kan geen emails versturen. Synchroniseer een e-mailadres om emails te kunnen versturen.',
  genericErrorMessage:
    'Deze flow bevat fouten. Ga naar de flow om deze op te lossen.',
};

export const isDeleted = (contact: {
  status: ContactStatus | null | undefined;
  id: string;
}): boolean => {
  const markedForDeletionContactIds =
    markedForDeletionContactList.getMarkedForDeletionContacts();

  return (
    (contact.status && contact.status === 'DELETING') ||
    markedForDeletionContactIds.includes(contact.id)
  );
};
export type FlowListData = {
  id: string;
  name: string;
  description?: string | null;
  enabled: boolean;
  createdDate: Date;
  lastModified: Date | null;
  shared: {
    enabled: boolean;
    id: string;
  };
  Statistics: Omit<FlowV2_Statistics, 'leadScoreAverage'> | null;
  errorMessage: string | null;
  trigger: {
    flowId: string;
    actionId: string;
    label: string;
  } | null;
};

export type FlowItems = Array<
  GetFlowsV2Query['getFlowsV2']['items'][number] & {
    isMissingEmailToSendFrom: boolean;
  }
>;

const getErrorMessage = ({
  hasValidationErrors,
  isMissingEmailToSendFrom,
}: {
  hasValidationErrors: boolean;
  isMissingEmailToSendFrom: boolean;
}): string | null => {
  if (isMissingEmailToSendFrom) {
    return text.cannotSendEmails;
  }

  if (hasValidationErrors) {
    return text.genericErrorMessage;
  }

  return null;
};

export const composeFlowListData = (
  items: FlowItems | null | undefined,
  variables: GetFlowsV2QueryVariables,
  version: string,
  sortSettings: SortSettings<Array<string>>,
  triggerLabels: Array<{
    flowId: string;
    actionId: string;
    label: string;
  }>,
): Array<FlowListData> => {
  if (items == null) return [];

  // @ts-ignore I couldn't explain TS what we're doing here, so ignore for now.
  const sorter = (sortSettings.desc ? descend : ascend)(path(sortSettings.id));

  return items
    .map(
      ({
        name,
        description,
        enabled,
        id,
        createdDate,
        lastModified,
        Statistics,
        share,
        StartAction,
        ValidationErrors,
        isMissingEmailToSendFrom,
      }) => ({
        id,
        name,
        description,
        enabled,
        createdDate: new Date(createdDate),
        lastModified: lastModified?.date ? new Date(lastModified.date) : null,
        Statistics: Statistics || null,
        shared: {
          enabled: !isNil(share),
          id,
        },
        trigger:
          triggerLabels.find(
            label => label.flowId === id && label.actionId === StartAction?.id,
          ) || null,
        errorMessage: getErrorMessage({
          isMissingEmailToSendFrom,
          hasValidationErrors: ValidationErrors.length > 0,
        }),
      }),
    )
    .sort((a, b) => {
      const lastKey = last(sortSettings.id) as string;
      const getFieldValue = (item: FlowListData) => {
        if (lastKey === 'Statistics') {
          return item.Statistics?.completedFlowInstancesTotal ?? 0;
        }

        if (lastKey === 'trigger') {
          return item.trigger?.label || '';
        }

        // For other fields, use path to get the value
        const pathValue = path(sortSettings.id, item);
        return pathValue ?? '';
      };

      const _a = {
        ...a,
        [lastKey]: getFieldValue(a),
      };
      const _b = {
        ...b,
        [lastKey]: getFieldValue(b),
      };

      return sorter(_a, _b);
    });
};

const memoizedComposeFlowListData = memoize(
  composeFlowListData,
  (
    items: Array<any>,
    variables: GetFlowsV2QueryVariables,
    version: string,
    sortSettings: SortSettings<string>,
    triggerLabels: Array<{
      flowId: string;
      actionId: string;
      label: string;
    }>,
  ) => {
    const triggerLabelsKey = triggerLabels
      .map(label => `${label.flowId}:${label.actionId}:${label.label}`)
      .join('|');
    const result = `${version}*@@*${items.length}*@@*${sortSettings.id}*@@*${
      sortSettings.desc ? SortDirection.Desc : SortDirection.Asc
    }*@@*${variables.nextToken || 'null'}*@@*${triggerLabelsKey}`;

    return result;
  },
);

export default memoizedComposeFlowListData;
